import { sortBy } from "lodash-es";

import { UserRoleEnum, type User, type UserPosition } from "@/api/user/models";
import type { UserDepartment } from "@/types/department";

type Nill<T> = T | null | undefined;

/** Retorna a primeira posição do usuário por data de criação. */
export function getFirstUserPosition(user: Nill<Pick<User, "positions">>): UserPosition | null {
  return sortBy(user?.positions ?? [], (position) => new Date(position.createdDate).getTime())[0] ?? null;
}

/** Retorna o departamento da primeira posição do usuário. */
export function getFirstUserDepartment(user: Nill<Pick<User, "positions">>): UserDepartment | null {
  return getFirstUserPosition(user)?.department ?? null;
}

/** Retorna a posição do usuário no departamento informado. */
export function getUserPositionByDepartment(
  user: Nill<Pick<User, "positions">>,
  department: Nill<UserDepartment>,
): UserPosition | null {
  if (!user || !department) {
    return null;
  }

  return user?.positions?.find((position) => position.department === department) ?? null;
}

/** Retorna o perfil do usuário no departamento informado. */
export function getUserRoleByDepartment(
  user: Nill<Pick<User, "positions">>,
  department: Nill<UserDepartment>,
): UserRoleEnum | null {
  return getUserPositionByDepartment(user, department)?.role ?? null;
}

/** Verifica se o produtor está indisponível na posição ativa. */
export function isProducerUnavailableInDepartment(
  user: Nill<Pick<User, "positions">>,
  department: Nill<UserDepartment>,
): boolean {
  const position = getUserPositionByDepartment(user, department);

  return position?.role === UserRoleEnum.PRODUCER && position.isAvailable === false;
}

/** Verifica se o usuário possui posição no departamento. */
export function userHasDepartment(user: Nill<Pick<User, "positions">>, department: Nill<UserDepartment>): boolean {
  return Boolean(getUserPositionByDepartment(user, department));
}

/** Verifica se o usuário possui qualquer um dos perfis globais. */
export function userHasAnyRole(user: Nill<Pick<User, "positions">>, roles: readonly UserRoleEnum[]): boolean {
  return user?.positions.some((position) => roles.includes(position.role)) ?? false;
}

/** Verifica se o usuário possui um dos perfis no departamento. */
export function userHasRoleInDepartment(
  user: Nill<Pick<User, "positions">>,
  department: Nill<UserDepartment>,
  roles: readonly UserRoleEnum[],
): boolean {
  const role = getUserRoleByDepartment(user, department);

  return Boolean(role && roles.includes(role));
}
